import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { SessionMonitorCard } from '../../components/SessionMonitorCard';

export const LiveMonitoring: React.FC = () => {
  const { workspace } = useWorkspace();
  const sessions = useQuery(api.sessions.listActiveByWorkspace, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );
  
  const assessments = useQuery(api.assessments.list, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );

  const assessmentMap = useMemo(() => {
    if (!assessments) return new Map();
    return new Map(assessments.map((a: Doc<"assessments">) => [a._id, a]));
  }, [assessments]);

  useEffect(() => {
    document.title = 'LibreRank - Live Monitoring';
  }, []);

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getTimeSince = (timestamp: number) => {
    return formatTime(Date.now() - timestamp);
  };

  const getStatusColor = (lastHeartbeat: number) => {
    const diff = Date.now() - lastHeartbeat;
    if (diff < 30000) return 'success'; // Active in last 30s
    if (diff < 120000) return 'warning'; // Active in last 2m
    return 'destructive'; // Inactive
  };

  const getStatusLabel = (lastHeartbeat: number) => {
    const diff = Date.now() - lastHeartbeat;
    if (diff < 30000) return 'Online';
    if (diff < 120000) return 'Idle';
    return 'Disconnected';
  };

  return (
    <WorkspaceAdminLayout title="Live Monitoring">
      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-muted-foreground">
            Monitor ongoing assessment sessions in real-time.
          </p>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-success"></span>
            <span className="text-sm text-muted-foreground">Active</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-warning"></span>
            <span className="text-sm text-muted-foreground">Idle</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-destructive"></span>
            <span className="text-sm text-muted-foreground">Inactive</span>
          </div>
        </div>
      </div>

      {!sessions ? (
        <div className="p-12 text-center bg-card border border-border rounded-xl">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading sessions...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="p-16 text-center bg-card border border-border border-dashed rounded-xl">
          <div className="bg-muted w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
            📡
          </div>
          <h3 className="text-lg font-medium mb-1">No active sessions</h3>
          <p className="text-muted-foreground">
            Active assessments will appear here as users start them.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sessions.map((session: Doc<"assessment_sessions">) => (
            <SessionMonitorCard 
              key={session._id}
              session={session}
              assessmentTitle={assessmentMap.get(session.assessmentId)?.title || 'Unknown Assessment'}
              getTimeSince={getTimeSince}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
            />
          ))}
        </div>
      )}
    </WorkspaceAdminLayout>
  );
};

